import { useEffect, useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const withTimeout = (promise, ms) => Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error('timed out')), ms))]);
const fail = (e) => `${e?.name === 'TypeError' ? 'blocked or network error' : e?.message || 'failed'}`;

const checkSecure = async () => {
  const https = window.location.protocol === 'https:';
  return { ok: https, detail: https ? 'HTTPS' : `served over ${window.location.protocol}` };
};

const checkApi = async () => {
  const t0 = performance.now();
  try {
    const res = await withTimeout(fetch(`${API}/api/status/ping`, { cache: 'no-store' }), 8000);
    const ms = Math.round(performance.now() - t0);
    if (!res.ok) return { ok: false, detail: `HTTP ${res.status} after ${ms} ms` };
    const body = await res.json();
    return { ok: body.ok === true, detail: `responded in ${ms} ms` };
  } catch (e) {
    return { ok: false, detail: fail(e) };
  }
};

const checkAssets = async () => {
  const targets = ['/favicon.png', '/fonts/RedHatText-latin.woff2'];
  const results = await Promise.all(targets.map(async (p) => {
    try {
      const r = await withTimeout(fetch(`${p}?t=${Date.now()}`, { cache: 'no-store' }), 8000);
      return r.ok ? null : `${p}: HTTP ${r.status}`;
    } catch (e) {
      return `${p}: ${fail(e)}`;
    }
  }));
  const problems = results.filter(Boolean);
  return { ok: problems.length === 0, detail: problems.length ? problems.join('; ') : 'images and fonts loaded' };
};

const checkStorage = async () => {
  try {
    localStorage.setItem('resiax_status_probe', '1');
    localStorage.removeItem('resiax_status_probe');
    return { ok: true, detail: 'browser storage available' };
  } catch {
    return { ok: false, detail: 'browser storage disabled — sign-in and saved votes will not persist' };
  }
};

export const CHECKS = [
  { name: 'Website reachable', run: async () => ({ ok: true, detail: 'this page loaded' }) },
  { name: 'Secure connection', run: checkSecure },
  { name: 'API reachable', run: checkApi },
  { name: 'Assets load', run: checkAssets },
  { name: 'Browser storage', run: checkStorage },
];

export const useAccessChecks = () => {
  const [results, setResults] = useState(CHECKS.map((c) => ({ name: c.name, status: 'pending' })));
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      for (let i = 0; i < CHECKS.length; i += 1) {
        const r = await CHECKS[i].run();
        if (cancelled) return;
        setResults((prev) => prev.map((p, j) => (j === i ? { name: p.name, status: r.ok ? 'ok' : 'fail', detail: r.detail } : p)));
      }
      if (!cancelled) setDone(true);
    })();
    return () => { cancelled = true; };
  }, []);

  return { results, done };
};
